import { useEffect, useState } from 'react'
import { AppBar, Box, Toolbar, Typography, useMediaQuery } from '@mui/material'
import { useQuery } from 'react-query'
import Link from 'next/link'

import Button from 'components/Button'
import { useRouter } from 'next/router'
import SocialMedia from './SocialMedia'
import MobileMenu from './MobileMenu'
import {
  HamburgerBar,
  HamburgerIconContainer,
  NavLink,
  NavContainer,
  EmergencyBannerContainer,
} from './styled'

import StarsIcon from './StarsIcon'

import {
  careersPath,
  emergencyBannerColorKey,
  emergencyBannerEnabledKey,
  emergencyBannerMessageKey,
  HamburgerBarsPosition,
  links,
  showWeAreHiringButtonKey,
} from './constants'
import ConstanceApi from 'api/ConstanceApi'
import { scrollToElement } from 'utils/pageUtils'
import { jobOpeningsId } from 'shared/constants'
import Image from 'next/image'
import useBookACallUrl from 'hooks/useBookACallUrl'
import { useSiteSettings } from 'hooks/useSiteSettings'
import CloseIcon from '@mui/icons-material/Close'

const Header = () => {
  const { bookACallUrl } = useBookACallUrl()

  useSiteSettings()

  const { data } = useQuery({
    queryKey: ['constancej'],
    queryFn: () =>
      ConstanceApi.getConstance([
        showWeAreHiringButtonKey,
        emergencyBannerEnabledKey,
        emergencyBannerMessageKey,
        emergencyBannerColorKey,
      ]),
  })

  const getContConfigsValues = <K extends ConstanceKey>(key: K): ConstanceKeyMap[K] | undefined => {
    const item = data?.find((item): item is IConstance<K> => item.key === key)

    return item?.value
  }

  const emergencyEnabled = getContConfigsValues(emergencyBannerEnabledKey)

  const emergencyBannerContent = getContConfigsValues(emergencyBannerMessageKey)

  const emergencyBannerColor = getContConfigsValues(emergencyBannerColorKey)

  const router = useRouter()
  const isCareersPage = router.pathname === careersPath

  const handleWeAreHiringClick = () => {
    if (isCareersPage) {
      scrollToElement(`#${jobOpeningsId}`, { block: 'start' })
    } else {
      router.push(`${careersPath}?register=true`)
    }
  }

  const up1000 = useMediaQuery('(min-width:1000px)')

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [leftPosition, setLeftPosition] = useState('-100%')
  const [emergencyBannerDismissed, setEmergencyBannerDismissed] = useState(false)

  useEffect(() => {
    if (isMenuOpen) {
      setLeftPosition('0')
    } else {
      setLeftPosition('-100%')
    }
  }, [isMenuOpen])

  return (
    <Box component="div">
      {isCareersPage && emergencyEnabled && emergencyBannerContent && !emergencyBannerDismissed && (
        <EmergencyBannerContainer bannerBackgroundColor={emergencyBannerColor ?? '#000'}>
          <Typography
            variant="subtitle2"
            sx={{ marginLeft: 'auto', marginRight: 'auto', textAlign: 'center' }}
          >
            {emergencyBannerContent}
          </Typography>
          <CloseIcon
            onClick={() => setEmergencyBannerDismissed(true)}
            sx={(theme) => ({
              marginLeft: theme.spacing(1),
              color: theme.palette.surface.light,
              cursor: 'pointer',
            })}
          />
        </EmergencyBannerContainer>
      )}
      <AppBar
        position="fixed"
        component="header"
        sx={{
          height: '5rem',
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          boxShadow: 'none',
          backdropFilter: 'blur(7.5px)',
          padding: [0, 0, '0 24px'],
        }}
      >
        <Toolbar sx={{ height: '100%' }}>
          <Box
            component="div"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Box component="div" sx={{ cursor: 'pointer' }}>
              <Link passHref href="/">
                <Box component="div">
                  <Image src={'/assets/logo.webp'} width={182} height={36} alt="logo" />
                </Box>
              </Link>
            </Box>
            {up1000 && (
              <NavContainer>
                {links.map((link, index) => (
                  <Link passHref href={link.link} key={index}>
                    <NavLink
                      title={link.title}
                      style={{ textDecoration: 'none' }}
                      isActive={router.pathname === link.link}
                    >
                      {link.title}
                    </NavLink>
                  </Link>
                ))}
                {data && data?.length > 0 && data[0].value && (
                  <Button
                    onClick={handleWeAreHiringClick}
                    sx={{ background: 'transparent' }}
                    buttonvariant="cta2"
                  >
                    <StarsIcon
                      fontSize="small"
                      sx={{ height: '11px', width: '10.5px', marginRight: '0.5rem' }}
                    />
                    <Typography variant="button" fontWeight={600} sx={{ textTransform: 'none' }}>
                      We are hiring!
                    </Typography>
                  </Button>
                )}
                <Button
                  buttonvariant="cta2"
                  href={bookACallUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Book a call
                </Button>
              </NavContainer>
            )}
          </Box>

          {!up1000 && (
            <HamburgerIconContainer onClick={() => setIsMenuOpen(!isMenuOpen)}>
              <Box component="div" sx={{ width: '18px', height: '12px', cursor: 'pointer' }}>
                {HamburgerBarsPosition.map((position, index) => (
                  <HamburgerBar key={index} order={position} open={isMenuOpen} />
                ))}
              </Box>
            </HamburgerIconContainer>
          )}
        </Toolbar>
      </AppBar>

      {up1000 ? (
        <Box
          component="div"
          sx={{ position: 'fixed', top: 'calc(50% - 96px)', right: '4.25rem', zIndex: 3 }}
        >
          <SocialMedia
            sx={{
              flexDirection: 'column',
              marginLeft: 0,
              marginTop: 0,
              marginBottom: 0,
              gap: '2rem',
            }}
          />
        </Box>
      ) : (
        <MobileMenu leftposition={leftPosition} setIsMenuOpen={setIsMenuOpen} />
      )}
    </Box>
  )
}

export default Header
